import { getCropRect } from './geometry';
import type { Transform } from './state';

/**
 * Everything needed to paint one frame. This is a pure description of a
 * frame so the same function can drive the live preview *and* an offline
 * frame-by-frame encoder (step 2, ffmpeg.wasm) with identical output.
 */
export interface SceneFrame {
  imgA: HTMLCanvasElement;
  imgB: HTMLCanvasElement;
  transformA: Transform;
  transformB: Transform;
  sliderX: number;
  showGuide: boolean;
  showLabels: boolean;
  /** Caption drawn at the bottom of the frame (e.g. "Results may vary"). */
  disclaimer?: string | null;
  /** Practice branding drawn in a bottom corner, on top of everything. */
  watermark?: Watermark | null;
}

export type WatermarkCorner = 'bottom-left' | 'bottom-right';

export interface Watermark {
  /** Handle or practice name, e.g. "@saenzaesthetics". */
  text: string | null;
  /** Pre-decoded logo (already downscaled), or null. */
  logo: HTMLCanvasElement | null;
  corner: WatermarkCorner;
  /** Logo width as a fraction of the frame width. */
  logoScale: number;
}

function coverDraw(
  ctx: CanvasRenderingContext2D,
  img: HTMLCanvasElement,
  cw: number,
  ch: number,
  transform: Transform,
): void {
  const { sx, sy, sw, sh } = getCropRect(img, cw, ch, transform);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
}

/**
 * Faint dashed reference overlay (face oval, eyes, nose, mouth) at fixed
 * generic proportions. One shared overlay: aligning each photo to it
 * independently aligns the two photos to each other.
 */
export function drawFaceGuide(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.65)';
  ctx.lineWidth = Math.max(1, w * 0.0025);
  ctx.setLineDash([w * 0.012, w * 0.01]);

  const cx = w / 2;
  const cy = h * 0.5;
  const rx = w * 0.2;
  const ry = h * 0.29;

  // face oval
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();

  // eyes
  const eyeY = cy - ry * 0.18;
  const eyeDX = rx * 0.48;
  const eyeRX = rx * 0.24;
  const eyeRY = ry * 0.09;
  ctx.beginPath();
  ctx.ellipse(cx - eyeDX, eyeY, eyeRX, eyeRY, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(cx + eyeDX, eyeY, eyeRX, eyeRY, 0, 0, Math.PI * 2);
  ctx.stroke();

  // nose
  ctx.beginPath();
  ctx.moveTo(cx, eyeY + eyeRY * 1.2);
  ctx.lineTo(cx, cy + ry * 0.32);
  ctx.stroke();

  // mouth
  const mouthY = cy + ry * 0.55;
  ctx.beginPath();
  ctx.moveTo(cx - rx * 0.32, mouthY);
  ctx.lineTo(cx + rx * 0.32, mouthY);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.restore();
}

/** Paint a complete frame onto `ctx` (whose canvas defines the output size). */
export function drawScene(ctx: CanvasRenderingContext2D, frame: SceneFrame): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const { sliderX } = frame;
  ctx.clearRect(0, 0, w, h);

  // "after" underneath, "before" clipped to the left of the divider
  coverDraw(ctx, frame.imgB, w, h, frame.transformB);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, sliderX, h);
  ctx.clip();
  coverDraw(ctx, frame.imgA, w, h, frame.transformA);
  ctx.restore();

  if (frame.showGuide) drawFaceGuide(ctx, w, h);

  // divider line
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.lineWidth = Math.max(2, w * 0.004);
  ctx.beginPath();
  ctx.moveTo(sliderX, 0);
  ctx.lineTo(sliderX, h);
  ctx.stroke();

  // handle
  const hy = h / 2;
  const r = Math.max(16, w * 0.028);
  ctx.beginPath();
  ctx.arc(sliderX, hy, r, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(30,27,22,0.25)';
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = '#1E1B16';
  ctx.font = `${Math.round(r * 0.9)}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('⇔', sliderX, hy + 1);

  if (frame.showLabels) drawLabels(ctx, w, h, sliderX);
  if (frame.disclaimer) drawDisclaimer(ctx, w, h, frame.disclaimer);
  if (frame.watermark && (frame.watermark.text || frame.watermark.logo)) drawWatermark(ctx, w, h, frame.watermark);
}

/** Watermark text size as a fraction of the frame width (a step below the corner labels). */
const WATERMARK_TEXT_SCALE = 0.035;
/** Side margin for the watermark as a fraction of the frame width. */
const WATERMARK_SIDE = 0.04;
/** Logo may not exceed this fraction of the frame height. */
const WATERMARK_MAX_LOGO_H = 0.2;

/**
 * Practice branding in a bottom corner: text on the baseline shared with the
 * disclaimer's bottom edge, logo stacked above it. Drawn last and never
 * clipped by the divider, so it survives every frame of the wipe.
 */
function drawWatermark(ctx: CanvasRenderingContext2D, w: number, h: number, wm: Watermark): void {
  const right = wm.corner === 'bottom-right';
  const margin = Math.round(w * WATERMARK_SIDE);
  let y = h - Math.round(h * DISCLAIMER_BOTTOM);
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = Math.max(2, w * 0.01);
  ctx.shadowOffsetY = Math.max(1, w * 0.002);

  const text = wm.text?.trim();
  if (text) {
    let fs = Math.max(14, Math.round(w * WATERMARK_TEXT_SCALE));
    const maxTextW = w * 0.45;
    for (let i = 0; i < 8; i++) {
      ctx.font = `600 ${fs}px sans-serif`;
      if (ctx.measureText(text).width <= maxTextW || fs <= 11) break;
      fs = Math.max(11, Math.round(fs * 0.85));
    }
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = right ? 'right' : 'left';
    ctx.fillStyle = 'rgba(255,255,255,0.94)';
    ctx.fillText(text, right ? w - margin : margin, y);
    y -= Math.round(fs * 1.35);
  }

  if (wm.logo && wm.logo.width > 0 && wm.logo.height > 0) {
    const ratio = wm.logo.height / wm.logo.width;
    let lw = w * Math.max(0.05, Math.min(0.5, wm.logoScale));
    let lh = lw * ratio;
    const maxH = h * WATERMARK_MAX_LOGO_H;
    if (lh > maxH) {
      lh = maxH;
      lw = lh / ratio;
    }
    ctx.globalAlpha = 0.92;
    ctx.drawImage(wm.logo, right ? w - margin - lw : margin, y - lh, lw, lh);
  }
  ctx.restore();
}

/** Disclaimer caption size as a fraction of the frame width. */
const DISCLAIMER_SCALE = 0.03;
/** Distance of the caption's bottom edge from the frame bottom, as a fraction of height. */
const DISCLAIMER_BOTTOM = 0.06;

/**
 * Small centered caption on a translucent pill near the bottom edge, on top
 * of everything and not clipped by the divider. Long text shrinks to fit.
 */
function drawDisclaimer(ctx: CanvasRenderingContext2D, w: number, h: number, text: string): void {
  let fs = Math.max(14, Math.round(w * DISCLAIMER_SCALE));
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const maxTextW = w * 0.86;
  let tw = 0;
  for (let i = 0; i < 8; i++) {
    ctx.font = `500 ${fs}px sans-serif`;
    tw = ctx.measureText(text).width;
    if (tw <= maxTextW || fs <= 11) break;
    fs = Math.max(11, Math.round(fs * 0.85));
  }
  const padX = fs * 0.8;
  const padY = fs * 0.45;
  const pillW = Math.min(w * 0.94, tw + padX * 2);
  const pillH = fs + padY * 2;
  const cx = w / 2;
  const cy = h - Math.round(h * DISCLAIMER_BOTTOM) - pillH / 2;

  ctx.fillStyle = 'rgba(30,27,22,0.55)';
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(cx - pillW / 2, cy - pillH / 2, pillW, pillH, pillH / 2);
  else ctx.rect(cx - pillW / 2, cy - pillH / 2, pillW, pillH);
  ctx.fill();
  ctx.clip();
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.fillText(text, cx, cy + fs * 0.04);
  ctx.restore();
}

/** Corner label size as a fraction of the frame width. */
const LABEL_SCALE = 0.055;

/**
 * "BEFORE" / "AFTER" corner labels. Each label belongs to its photo and is
 * clipped to that photo's side of the divider, so the divider wipes the
 * label together with the image it describes: at the far left only AFTER
 * is visible, at the far right only BEFORE.
 */
function drawLabels(ctx: CanvasRenderingContext2D, w: number, h: number, sliderX: number): void {
  const fs = Math.max(20, Math.round(w * LABEL_SCALE));
  const pad = Math.round(fs * 0.6);
  ctx.save();
  ctx.font = `700 ${fs}px sans-serif`;
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'rgba(255,255,255,0.94)';
  // soft shadow keeps the white text legible over bright skin or backdrops
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = fs * 0.25;
  ctx.shadowOffsetY = fs * 0.05;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, sliderX, h);
  ctx.clip();
  ctx.textAlign = 'left';
  ctx.fillText('BEFORE', pad, pad);
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.rect(sliderX, 0, w - sliderX, h);
  ctx.clip();
  ctx.textAlign = 'right';
  ctx.fillText('AFTER', w - pad, pad);
  ctx.restore();

  ctx.restore();
}
